import { sendSafeError } from '#src/lib/error.ts';

const RECAPTCHA_V3_SITE_KEY = '6LdFw9wUAAAAAPlokjeCEd0rReOxre5ys52pUfSi';
const RECAPTCHA_V3_SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`;
const RECAPTCHA_V3_TIMEOUT_MS = 5000;

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const SCRIPT_SELECTOR = `script[src="${RECAPTCHA_V3_SCRIPT_URL}"]`;

const createScript = () => {
  const script = document.createElement('script');
  script.src = RECAPTCHA_V3_SCRIPT_URL;
  script.async = true;
  return script;
};

const loadScript = async () => {
  const existingScript =
    document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
  if (existingScript?.dataset.loaded) {
    return;
  }

  const script = existingScript ?? createScript();

  await new Promise<void>((resolve, reject) => {
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      'error',
      () => {
        script.remove();
        reject(new Error('Could not load the reCAPTCHA v3 script'));
      },
      { once: true }
    );
    if (!existingScript) {
      document.head.appendChild(script);
    }
  });
};

const requestToken = async (action: string) => {
  await loadScript();
  const { grecaptcha } = window as Window & { grecaptcha?: Grecaptcha };
  if (!grecaptcha) {
    throw new Error('reCAPTCHA v3 is not available');
  }

  return new Promise<string>((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(RECAPTCHA_V3_SITE_KEY, { action })
        .then(resolve, reject);
    });
  });
};

const rejectAfterTimeout = async (timeoutMs: number) =>
  new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('reCAPTCHA v3 timed out')), timeoutMs);
  });

export const executeRecaptchaV3 = async (
  action: string
): Promise<string | undefined> => {
  try {
    return await Promise.race([
      requestToken(action),
      rejectAfterTimeout(RECAPTCHA_V3_TIMEOUT_MS),
    ]);
  } catch (error) {
    sendSafeError(error);
    return undefined;
  }
};
